// Data-subject rights — export what the organisation holds, and start an
// account closure.
//
// TWO THINGS THIS FILE IS CAREFUL ABOUT, both of which used to be wrong:
//
//  1. THE EXPORT MUST DESCRIBE *THIS* ORGANISATION. The live screen previously
//     built its download from `src/domain/fixtures` — so a real admin clicking
//     Export downloaded a file full of another (fictional, but shaped exactly
//     like real) organisation's member emails. On a data-rights screen that is
//     the worst possible bug. The live path now composes strictly from
//     org-scoped reads (orgOverview / orgMembers / orgConsentLog / orgRoster /
//     licenses) for `useOrg().orgId`, and a local allowlist guard
//     ({@link assertLiveExportSafe}) refuses to serialize anything else —
//     including a value that even *looks* like a child UUID.
//
//  2. NOTHING MAY CLAIM TO HAVE CLOSED AN ACCOUNT THAT IS STILL OPEN. The old
//     "Close organisation account" flow was a `setState` that swapped in the
//     words "Organisation account closed." No row was deleted, no licence was
//     settled, nobody was told. It now says what is true: closure is handled by
//     the Zooylo team, confirming *opens a request*, and the notice afterwards
//     says explicitly that nothing has been closed yet.
//
// Demo (fixtures) and Live (Supabase) share one presentational view. The demo
// branch keeps the existing fixture-backed export via `buildOrgExport`, whose
// contract is covered by src/domain/exportData.test.ts.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useComfort } from "../ComfortContext";
import { getDictionary } from "../../i18n/dictionary";
import { buildOrgExport, toCsv, toJson, toCsvBundle } from "../../domain/exportData";
import { getPortalData, shouldUseSupabase } from "../../data";
import { PortalWriteError } from "../../data/supabaseAdapter";
import type {
  License,
  OrgConsentEntry,
  OrgMemberEntry,
  OrgOverview,
  OrgRosterEntry,
} from "../../data/types";
import { useOrg } from "./OrgContext";
import {
  organisation,
  accounts,
  memberships,
  seats as fixtureSeats,
  childLinks,
  consents,
  children,
} from "../../domain/fixtures";

/**
 * Where an account-closure request goes. Closure is a manual, human process
 * (records removed properly, licence settled), so the portal's job is to open a
 * clearly-addressed request — never to pretend it did the work itself.
 */
export const SUPPORT_EMAIL = "support@zooylo.app";

// ---- Local strings ----------------------------------------------------------

const L = {
  en: {
    exportPanelTitle: "Export organisation data",
    exportNote:
      "What's exported: member emails, seat records, link audit, consent log, and child display labels. Children appear only as pseudonymous UUIDs — there is no child personal data held on our servers.",
    exportNoteLive:
      "What's exported: your members and their roles, seat and licence records, and the consent audit trail. Children appear only as an opaque reference — we hold no child names, labels or progress, so there is none to export.",
    formatLabel: "Format",
    formatJson: "JSON (full export)",
    formatCsv: "CSV (choose section)",
    sectionLabel: "Section",
    sectionMembers: "Members",
    sectionSeats: "Seats",
    sectionLinks: "Links",
    sectionConsentLog: "Consent log",
    sectionChildren: "Children",
    sectionRoster: "Children (opaque refs)",
    downloadButton: "Download",
    downloadedNote: "Downloaded — check your downloads folder.",
    loadingExport: "Preparing your export…",
    exportErrorRetry: "Try again",
    summaryLabel: "Ready to export",
    summaryMembers: "members",
    summarySeats: "seats",
    summaryChildren: "children (opaque refs)",
    summaryConsents: "consent records",
    emptyExportNote:
      "Your organisation is new, so this export is mostly empty — just you and your licence. That's exactly as it should be.",
    requestsTitle: "Member data requests",
    requestsBody:
      "If a member asks for a copy of their personal data or for their account to be deleted, process the request via your registered admin email. The export above covers everything the organisation holds.",
    requestsNote:
      "Personal data per member is limited to their email address and activity recorded in this portal.",
    closeSubtitle:
      "Closing an organisation is handled by the Zooylo team, so records are removed properly and your licence is settled. Confirming below opens a request to us — it does not close anything on its own. Children's records live on their own devices and are not held on our servers.",
    closeButtonHint: "Opens a request to the Zooylo team",
    closeMembersIntro: "Everyone who would lose access when a closure completes:",
    closeMembersLoading: "Loading who has access…",
    closeMembersEmpty: "You're the only person with access at the moment.",
    closeTypePrompt: "Type your organisation name to confirm:",
    closeTypeHint: "We'll ask you to confirm again by email before anything is removed.",
    closeButton: "Close organisation account",
    closeConfirmTitle: "Close this organisation account?",
    closeConfirmBody:
      "This opens a closure request addressed to the Zooylo team. Nothing is closed, removed or cancelled yet — we'll reply to your admin email to agree what happens and when.",
    closeConfirmNote:
      "Member access, seats and your licence stay exactly as they are until you and we agree the closure. Child data is not held on our servers and is unaffected.",
    closeConfirmLabel: "Open closure request email",
    requestedTitle: "Nothing has been closed yet.",
    requestedBody:
      "We've prepared a closure request to {email} with your organisation name. Send it and we'll reply to your admin email to confirm the next steps. Your account, members and licence carry on as normal until then.",
    requestedLink: "Open the request email again",
    mailSubject: "Account closure request",
    mailBodyIntro:
      "Hello Zooylo team,\n\nPlease start the closure process for our organisation.",
    mailBodyOrg: "Organisation",
    mailBodyId: "Organisation reference",
    mailBodyOutro:
      "I understand you'll confirm with me by email before anything is removed.\n\nThank you.",
  },
  ms: {
    exportPanelTitle: "Eksport data organisasi",
    exportNote:
      "Apa yang dieksport: e-mel ahli, rekod kerusi, audit pautan, log keizinan, dan label paparan kanak-kanak. Kanak-kanak hanya muncul sebagai UUID nama samaran — tiada data peribadi kanak-kanak disimpan di pelayan kami.",
    exportNoteLive:
      "Apa yang dieksport: ahli anda dan peranan mereka, rekod kerusi dan lesen, serta jejak audit keizinan. Kanak-kanak hanya muncul sebagai rujukan legap — kami tidak menyimpan nama, label atau kemajuan kanak-kanak, jadi tiada apa untuk dieksport.",
    formatLabel: "Format",
    formatJson: "JSON (eksport penuh)",
    formatCsv: "CSV (pilih bahagian)",
    sectionLabel: "Bahagian",
    sectionMembers: "Ahli",
    sectionSeats: "Kerusi",
    sectionLinks: "Pautan",
    sectionConsentLog: "Log keizinan",
    sectionChildren: "Kanak-kanak",
    sectionRoster: "Kanak-kanak (rujukan legap)",
    downloadButton: "Muat turun",
    downloadedNote: "Dimuat turun — semak folder muat turun anda.",
    loadingExport: "Menyediakan eksport anda…",
    exportErrorRetry: "Cuba lagi",
    summaryLabel: "Sedia untuk dieksport",
    summaryMembers: "ahli",
    summarySeats: "kerusi",
    summaryChildren: "kanak-kanak (rujukan legap)",
    summaryConsents: "rekod keizinan",
    emptyExportNote:
      "Organisasi anda baharu, jadi eksport ini hampir kosong — hanya anda dan lesen anda. Itu memang sepatutnya.",
    requestsTitle: "Permintaan data ahli",
    requestsBody:
      "Jika ahli meminta salinan data peribadi atau pemadaman akaun, proses permintaan melalui e-mel pentadbir berdaftar anda. Eksport di atas merangkumi semua yang organisasi simpan.",
    requestsNote:
      "Data peribadi setiap ahli terhad kepada alamat e-mel dan aktiviti dalam portal ini.",
    closeSubtitle:
      "Penutupan organisasi diuruskan oleh pasukan Zooylo, supaya rekod dibuang dengan betul dan lesen anda diselesaikan. Pengesahan di bawah membuka permintaan kepada kami — ia tidak menutup apa-apa dengan sendirinya. Rekod kanak-kanak kekal pada peranti sendiri dan tidak disimpan di pelayan kami.",
    closeButtonHint: "Membuka permintaan kepada pasukan Zooylo",
    closeMembersIntro: "Semua yang akan hilang akses apabila penutupan selesai:",
    closeMembersLoading: "Memuatkan siapa yang mempunyai akses…",
    closeMembersEmpty: "Anda satu-satunya orang yang mempunyai akses buat masa ini.",
    closeTypePrompt: "Taip nama organisasi anda untuk mengesahkan:",
    closeTypeHint: "Kami akan minta pengesahan sekali lagi melalui e-mel sebelum apa-apa dibuang.",
    closeButton: "Tutup akaun organisasi",
    closeConfirmTitle: "Tutup akaun organisasi ini?",
    closeConfirmBody:
      "Ini membuka permintaan penutupan kepada pasukan Zooylo. Tiada apa-apa ditutup, dibuang atau dibatalkan lagi — kami akan membalas e-mel pentadbir anda untuk bersetuju apa yang berlaku dan bila.",
    closeConfirmNote:
      "Akses ahli, kerusi dan lesen anda kekal seperti sedia ada sehingga anda dan kami bersetuju dengan penutupan. Data kanak-kanak tidak disimpan di pelayan kami dan tidak terjejas.",
    closeConfirmLabel: "Buka e-mel permintaan penutupan",
    requestedTitle: "Tiada apa-apa ditutup lagi.",
    requestedBody:
      "Kami telah menyediakan permintaan penutupan kepada {email} dengan nama organisasi anda. Hantarkannya dan kami akan membalas e-mel pentadbir anda untuk mengesahkan langkah seterusnya. Akaun, ahli dan lesen anda berjalan seperti biasa sehingga itu.",
    requestedLink: "Buka semula e-mel permintaan",
    mailSubject: "Permintaan penutupan akaun",
    mailBodyIntro:
      "Salam pasukan Zooylo,\n\nSila mulakan proses penutupan untuk organisasi kami.",
    mailBodyOrg: "Organisasi",
    mailBodyId: "Rujukan organisasi",
    mailBodyOutro:
      "Saya faham anda akan mengesahkan dengan saya melalui e-mel sebelum apa-apa dibuang.\n\nTerima kasih.",
  },
} as const;

// ---- Types ------------------------------------------------------------------

type ExportFormat = "json" | "csv";
type LoadState = "loading" | "ready" | "error";

interface SectionOption {
  value: string;
  label: string;
}

interface AccessRow {
  email: string;
  role: string;
}

// ---- Live export composition ------------------------------------------------
//
// The live sources are the admin aggregate RPCs, not the tables — RLS correctly
// forbids an admin from reading child / child_link / consent rows. So the live
// export has a different (smaller, honest) shape to the fixtures one: there is
// no `links` section and no `children` section, because there is no live source
// for either. Instead the roster section carries the org-scoped opaque refs.

export interface LiveMemberRow {
  accountId: string;
  email: string;
  role: string;
  seatStatus: string;
  joinedAt: string;
}

export interface LiveSeatRow {
  accountId: string;
  status: string;
}

export interface LiveRosterRow {
  childRef: string;
  supportedBy: string[];
  sharingStatus: string;
  lastChange: string;
}

export interface LiveConsentRow {
  consentId: string;
  childRef: string;
  memberEmail: string;
  scope: string[];
  status: string;
  grantedAt: string;
  revokedAt: string;
}

export interface LiveOrgExport {
  generatedAt: string;
  organisation: { id: string; name: string; type: string };
  licence: {
    plan: string;
    period: string;
    seatsTotal: number;
    seatsUsed: number;
  } | null;
  members: LiveMemberRow[];
  seats: LiveSeatRow[];
  roster: LiveRosterRow[];
  consentLog: LiveConsentRow[];
}

export interface LiveExportInput {
  orgId: string;
  orgName: string;
  orgType: string;
  overview: OrgOverview | null;
  members: OrgMemberEntry[];
  roster: OrgRosterEntry[];
  consentLog: OrgConsentEntry[];
  licence: License | null;
}

/**
 * The EXACT set of fields each live export section may contain. Anything else
 * is a bug, and {@link assertLiveExportSafe} turns it into a loud failure rather
 * than a quiet leak. This mirrors EXPORT_ALLOWLIST in src/domain/exportData.ts
 * for the sections the live backend can actually serve.
 */
export const LIVE_EXPORT_ALLOWLIST = {
  members: ["accountId", "email", "role", "seatStatus", "joinedAt"],
  seats: ["accountId", "status"],
  roster: ["childRef", "supportedBy", "sharingStatus", "lastChange"],
  consentLog: [
    "consentId",
    "childRef",
    "memberEmail",
    "scope",
    "status",
    "grantedAt",
    "revokedAt",
  ],
} as const satisfies Record<string, readonly string[]>;

export type LiveExportSection = keyof typeof LIVE_EXPORT_ALLOWLIST;

const LIVE_SECTIONS = Object.keys(LIVE_EXPORT_ALLOWLIST) as LiveExportSection[];

/** Anything shaped like a UUID must never appear as a child reference. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Throws if a live export carries a field outside its allowlist, or a childRef
 * that is actually a UUID (which would mean an aggregate RPC regressed into
 * handing an admin a real child id).
 */
export function assertLiveExportSafe(exp: LiveOrgExport): void {
  for (const section of LIVE_SECTIONS) {
    const allowed: readonly string[] = LIVE_EXPORT_ALLOWLIST[section];
    const rows = exp[section] as unknown as ReadonlyArray<
      Record<string, unknown>
    >;
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (!allowed.includes(key)) {
          throw new Error(
            `Export field "${key}" in section "${section}" is not in the ` +
              `allowlist [${allowed.join(", ")}] — child PII must never reach an export.`,
          );
        }
      }
      const ref = row.childRef;
      if (typeof ref === "string" && UUID_RE.test(ref)) {
        throw new Error(
          `Export section "${section}" carries a child UUID where an opaque ` +
            `reference is required — refusing to serialize.`,
        );
      }
    }
  }
}

/**
 * Compose the live export from org-scoped reads only. Pure: no fixtures, no
 * I/O, same input → same output. Runs {@link assertLiveExportSafe} before
 * returning, so a caller can never serialize an unchecked value.
 */
export function buildLiveOrgExport(
  input: LiveExportInput,
  generatedAt: string,
): LiveOrgExport {
  const members: LiveMemberRow[] = input.members.map((m) => ({
    accountId: m.accountId,
    email: m.email,
    role: m.role,
    seatStatus: m.seatStatus ?? "none",
    joinedAt: m.joinedAt,
  }));

  const seats: LiveSeatRow[] = input.members
    .filter((m) => m.seatStatus !== null)
    .map((m) => ({ accountId: m.accountId, status: m.seatStatus as string }));

  const roster: LiveRosterRow[] = input.roster.map((r) => ({
    childRef: r.childRef,
    supportedBy: [...r.supportedBy],
    sharingStatus: r.sharingStatus,
    lastChange: r.lastChange ?? "",
  }));

  const consentLog: LiveConsentRow[] = input.consentLog.map((c) => ({
    consentId: c.consentId,
    childRef: c.childRef,
    memberEmail: c.memberEmail,
    scope: [...c.scope],
    status: c.status,
    grantedAt: c.grantedAt,
    revokedAt: c.revokedAt ?? "",
  }));

  const licence = input.licence
    ? {
        plan: input.licence.plan,
        period: input.licence.period,
        seatsTotal: input.licence.seatsTotal,
        seatsUsed: input.licence.seatsUsed,
      }
    : input.overview
      ? {
          plan: "",
          period: "",
          seatsTotal: input.overview.seatsTotal,
          seatsUsed: input.overview.seatsUsed,
        }
      : null;

  const exp: LiveOrgExport = {
    generatedAt,
    organisation: {
      id: input.orgId,
      name: input.overview?.orgName || input.orgName,
      type: input.overview?.orgType || input.orgType,
    },
    licence,
    members,
    seats,
    roster,
    consentLog,
  };

  assertLiveExportSafe(exp);
  return exp;
}

/** Each live section rendered to its own CSV (RFC 4180, via toCsv). */
export function toLiveCsvBundle(
  exp: LiveOrgExport,
): Record<LiveExportSection, string> {
  return {
    members: toCsv(exp.members),
    seats: toCsv(exp.seats),
    roster: toCsv(exp.roster),
    consentLog: toCsv(exp.consentLog),
  };
}

/** Calm copy for a failed read, mapped on SQLSTATE — never on message text. */
export function exportErrorCopy(err: unknown): string {
  const code = err instanceof PortalWriteError ? err.code : undefined;
  if (code === "42501") {
    return "You don't have permission to export this organisation's data. If that looks wrong, sign in again.";
  }
  if (code === "P0002") {
    return "We couldn't find this organisation. Try refreshing the page.";
  }
  if (code === "P0001") {
    return "We couldn't put your export together. Please try again in a moment.";
  }
  return "We couldn't load your organisation's data just now. Please try again.";
}

// ---- Helpers ----------------------------------------------------------------

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** The locale strings the closure-request email is composed from. */
export interface ClosureMailStrings {
  mailSubject: string;
  mailBodyIntro: string;
  mailBodyOrg: string;
  mailBodyId: string;
  mailBodyOutro: string;
}

/** The closure-request mailto, with the organisation pre-filled. */
export function closureMailto(
  orgName: string,
  orgId: string,
  l: ClosureMailStrings,
): string {
  const body = [
    l.mailBodyIntro,
    "",
    `${l.mailBodyOrg}: ${orgName}`,
    `${l.mailBodyId}: ${orgId}`,
    "",
    l.mailBodyOutro,
  ].join("\n");
  return (
    `mailto:${SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent(`${l.mailSubject} — ${orgName}`)}` +
    `&body=${encodeURIComponent(body)}`
  );
}

// ---- Table cell styles ------------------------------------------------------

const TH: React.CSSProperties = {
  padding: "10px 14px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: ".1em",
  textTransform: "uppercase",
  color: "var(--ink-faint)",
  borderBottom: "1px solid var(--line)",
  whiteSpace: "nowrap",
};

const TD: React.CSSProperties = {
  padding: "14px 14px",
  fontSize: 14.5,
  color: "var(--ink)",
  borderBottom: "1px solid var(--line)",
  verticalAlign: "middle",
};

// ---- Confirm modal ----------------------------------------------------------

function ConfirmModal({
  title,
  body,
  note,
  confirmLabel,
  confirmHref,
  cancelLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  note: string;
  confirmLabel: string;
  /** When present the confirm control is a real link (e.g. a mailto request). */
  confirmHref?: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const confirmStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 44,
    padding: "0 20px",
    border: 0,
    background: "var(--peach)",
    color: "var(--peach-deep)",
    fontFamily: "var(--font-body)",
    fontWeight: 700,
    fontSize: 14,
    borderRadius: "var(--r-pill)",
    cursor: "pointer",
    textDecoration: "none",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="data-rights-confirm-title"
      style={{
        position: "fixed",
        inset: 0,
        display: "grid",
        placeItems: "center",
        background: "rgba(0,0,0,.35)",
        zIndex: 1000,
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: "var(--surface)",
          borderRadius: "var(--r-xl, 20px)",
          padding: "28px 24px",
          width: "100%",
          maxWidth: 440,
          boxShadow: "0 8px 32px rgba(0,0,0,.18)",
        }}
      >
        <h2
          id="data-rights-confirm-title"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 20,
            color: "var(--ink-deep)",
            margin: "0 0 10px",
          }}
        >
          {title}
        </h2>
        <p style={{ fontSize: 15, color: "var(--ink-soft)", lineHeight: 1.6, margin: "0 0 4px" }}>
          {body}
        </p>
        <div
          style={{
            background: "var(--peach-tint)",
            border: "1px solid var(--peach)",
            borderRadius: "var(--r-lg)",
            padding: "13px 15px",
            margin: "14px 0 22px",
          }}
        >
          <p style={{ fontSize: 13.5, color: "var(--peach-deep)", margin: 0, lineHeight: 1.55 }}>
            {note}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
          <button
            onClick={onCancel}
            style={{
              minHeight: 44,
              padding: "0 20px",
              border: "1.5px solid var(--line)",
              background: "transparent",
              color: "var(--ink-soft)",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: 14,
              borderRadius: "var(--r-pill)",
              cursor: "pointer",
            }}
          >
            {cancelLabel}
          </button>
          {confirmHref ? (
            <a href={confirmHref} onClick={onConfirm} style={confirmStyle}>
              {confirmLabel}
            </a>
          ) : (
            <button onClick={onConfirm} style={confirmStyle}>
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Presentational view ----------------------------------------------------

export interface DataRightsViewProps {
  /** Typed by the admin to confirm a closure request. */
  orgName: string;
  orgId: string;
  /** Copy describing what the export contains on this path. */
  exportNote: string;
  sections: SectionOption[];
  /** Readiness of the data the export is composed from. */
  state: LoadState;
  errorMessage?: string;
  onRetry?: () => void;
  /** Optional "what's in it" counts — live only; the demo omits them. */
  summary?: string;
  /** Shown when the org holds nothing but its creator. */
  emptyNote?: string;
  onDownload: (format: ExportFormat, section: string) => void;
  /** Adults who would lose access — adult emails are org data, not child PII. */
  members: AccessRow[];
}

export function DataRightsView({
  orgName,
  orgId,
  exportNote,
  sections,
  state,
  errorMessage,
  onRetry,
  summary,
  emptyNote,
  onDownload,
  members,
}: DataRightsViewProps) {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const t = dict.admin.data;
  const common = dict.common;
  const l = L[locale];

  const [format, setFormat] = useState<ExportFormat>("json");
  const [csvSection, setCsvSection] = useState<string>(
    () => sections[0]?.value ?? "members",
  );
  const [downloaded, setDownloaded] = useState(false);

  const [closeInput, setCloseInput] = useState("");
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closureRequested, setClosureRequested] = useState(false);

  const ready = state === "ready";
  const closeEnabled = closeInput.trim() === orgName && orgName !== "";
  const mailto = closureMailto(orgName, orgId, l);

  const selectStyle: React.CSSProperties = {
    height: 44,
    padding: "0 14px",
    border: "1.5px solid var(--line)",
    borderRadius: "var(--r-md)",
    background: "var(--surface)",
    color: "var(--ink)",
    fontFamily: "var(--font-body)",
    fontSize: 14.5,
    cursor: "pointer",
  };

  return (
    <>
      {/* Page header */}
      <div>
        <h1 style={{ fontSize: 30, color: "var(--ink-deep)", letterSpacing: "-.02em", margin: 0 }}>
          {t.title}
        </h1>
        <p style={{ color: "var(--ink-soft)", marginTop: 8, fontSize: 16.5, margin: "8px 0 0" }}>
          {t.subtitle}
        </p>
      </div>

      {/* Export panel */}
      <div
        style={{
          marginTop: 24,
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          boxShadow: "var(--sh-1)",
          padding: "20px 20px 22px",
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 8px" }}>
          {l.exportPanelTitle}
        </h2>
        <p style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.6, margin: "0 0 18px" }}>
          {exportNote}
        </p>

        {state === "error" ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              background: "var(--peach-tint)",
              border: "1px solid var(--peach)",
              borderRadius: "var(--r-lg)",
              padding: "14px 16px",
            }}
          >
            <p style={{ fontSize: 14, color: "var(--peach-deep)", margin: 0, lineHeight: 1.55 }}>
              {errorMessage}
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                style={{
                  marginTop: 12,
                  minHeight: 40,
                  padding: "0 18px",
                  border: "1.5px solid var(--peach-deep)",
                  borderRadius: "var(--r-pill)",
                  background: "transparent",
                  color: "var(--peach-deep)",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                {l.exportErrorRetry}
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              {/* Format selector */}
              <div style={{ flex: "0 1 220px", display: "flex", flexDirection: "column", gap: 5 }}>
                <label
                  htmlFor="export-format"
                  style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-soft)" }}
                >
                  {l.formatLabel}
                </label>
                <select
                  id="export-format"
                  value={format}
                  onChange={(e) => {
                    setFormat(e.target.value as ExportFormat);
                    setDownloaded(false);
                  }}
                  style={selectStyle}
                >
                  <option value="json">{l.formatJson}</option>
                  <option value="csv">{l.formatCsv}</option>
                </select>
              </div>

              {/* CSV section selector */}
              {format === "csv" && (
                <div style={{ flex: "0 1 200px", display: "flex", flexDirection: "column", gap: 5 }}>
                  <label
                    htmlFor="export-section"
                    style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-soft)" }}
                  >
                    {l.sectionLabel}
                  </label>
                  <select
                    id="export-section"
                    value={csvSection}
                    onChange={(e) => {
                      setCsvSection(e.target.value);
                      setDownloaded(false);
                    }}
                    style={selectStyle}
                  >
                    {sections.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Download button — inert until the data is actually here */}
              <button
                onClick={() => {
                  if (!ready) return;
                  onDownload(format, csvSection);
                  setDownloaded(true);
                }}
                disabled={!ready}
                style={{
                  flex: "0 0 auto",
                  minHeight: 44,
                  padding: "0 22px",
                  border: 0,
                  borderRadius: "var(--r-pill)",
                  background: "var(--brand)",
                  color: "#fff",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: ready ? "pointer" : "not-allowed",
                  opacity: ready ? 1 : 0.5,
                  boxShadow: "var(--sh-1)",
                  alignSelf: "flex-end",
                }}
              >
                {l.downloadButton}
              </button>
            </div>

            {state === "loading" && (
              <p
                role="status"
                aria-live="polite"
                style={{ marginTop: 12, fontSize: 13.5, color: "var(--ink-soft)", margin: "12px 0 0" }}
              >
                {l.loadingExport}
              </p>
            )}

            {ready && summary && (
              <p
                role="status"
                aria-live="polite"
                style={{ marginTop: 12, fontSize: 13.5, color: "var(--ink-soft)", margin: "12px 0 0" }}
              >
                {summary}
              </p>
            )}

            {ready && emptyNote && (
              <p
                style={{
                  marginTop: 10,
                  fontSize: 13.5,
                  color: "var(--ink-soft)",
                  lineHeight: 1.55,
                  background: "var(--surface-sunk)",
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-md)",
                  padding: "11px 15px",
                }}
              >
                {emptyNote}
              </p>
            )}
          </>
        )}

        {downloaded && (
          <p
            role="status"
            aria-live="polite"
            style={{ marginTop: 10, fontSize: 13.5, color: "var(--sage-deep)", margin: "10px 0 0" }}
          >
            {l.downloadedNote}
          </p>
        )}
      </div>

      {/* Member data requests */}
      <div
        style={{
          marginTop: 18,
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          boxShadow: "var(--sh-1)",
          padding: "20px 20px 22px",
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 8px" }}>
          {l.requestsTitle}
        </h2>
        <p style={{ fontSize: 14.5, color: "var(--ink-soft)", lineHeight: 1.6, margin: "0 0 12px" }}>
          {l.requestsBody}
        </p>
        <div
          style={{
            padding: "11px 15px",
            background: "var(--surface-sunk)",
            border: "1px solid var(--line)",
            borderRadius: "var(--r-md)",
            fontSize: 13.5,
            color: "var(--ink-soft)",
            lineHeight: 1.55,
          }}
        >
          {l.requestsNote}
        </div>
      </div>

      {/* Close the organisation account — a REQUEST, and it says so */}
      <div
        style={{
          marginTop: 18,
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          boxShadow: "var(--sh-1)",
          padding: "20px 20px 22px",
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 8px" }}>
          {t.closeTitle}
        </h2>

        {closureRequested ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              padding: "14px 16px",
              background: "var(--peach-tint)",
              border: "1px solid var(--peach)",
              borderRadius: "var(--r-md)",
            }}
          >
            <p
              style={{
                fontSize: 14.5,
                color: "var(--peach-deep)",
                fontWeight: 700,
                margin: "0 0 6px",
              }}
            >
              {l.requestedTitle}
            </p>
            <p
              style={{
                fontSize: 14,
                color: "var(--peach-deep)",
                lineHeight: 1.6,
                margin: "0 0 10px",
              }}
            >
              {l.requestedBody.replace("{email}", SUPPORT_EMAIL)}
            </p>
            <a
              href={mailto}
              style={{ fontSize: 14, color: "var(--peach-deep)", fontWeight: 600 }}
            >
              {l.requestedLink}
            </a>
          </div>
        ) : (
          <>
            <p
              style={{ fontSize: 14.5, color: "var(--ink-soft)", lineHeight: 1.6, margin: "0 0 18px" }}
            >
              {l.closeSubtitle}
            </p>

            {/* Who would lose access */}
            <p style={{ fontSize: 13.5, color: "var(--ink-soft)", margin: "0 0 10px" }}>
              {l.closeMembersIntro}
            </p>
            {state === "loading" ? (
              <p
                role="status"
                aria-live="polite"
                style={{ fontSize: 13.5, color: "var(--ink-faint)", margin: "0 0 20px" }}
              >
                {l.closeMembersLoading}
              </p>
            ) : members.length === 0 ? (
              <p style={{ fontSize: 13.5, color: "var(--ink-faint)", margin: "0 0 20px" }}>
                {l.closeMembersEmpty}
              </p>
            ) : (
              <div
                style={{
                  background: "var(--surface-sunk)",
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-lg)",
                  overflow: "hidden",
                  marginBottom: 20,
                }}
              >
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{ width: "100%", borderCollapse: "collapse", minWidth: 320 }}
                    aria-label="Members who will lose access"
                  >
                    <thead>
                      <tr>
                        <th scope="col" style={TH}>
                          {dict.admin.members.colMember}
                        </th>
                        <th scope="col" style={TH}>
                          {dict.admin.members.colRole}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m, idx) => {
                        const isLast = idx === members.length - 1;
                        return (
                          <tr key={`${m.email}-${idx}`}>
                            <td
                              style={{
                                ...TD,
                                borderBottom: isLast ? "none" : TD.borderBottom,
                                fontSize: 14,
                              }}
                            >
                              {m.email}
                            </td>
                            <td
                              style={{
                                ...TD,
                                borderBottom: isLast ? "none" : TD.borderBottom,
                                fontSize: 13.5,
                                color: "var(--ink-soft)",
                              }}
                            >
                              {m.role}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Type to confirm */}
            <div style={{ display: "flex", flexDirection: "column", gap: 5, maxWidth: 420 }}>
              <label
                htmlFor="close-org-input"
                style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-soft)" }}
              >
                {l.closeTypePrompt}
              </label>
              <input
                id="close-org-input"
                type="text"
                value={closeInput}
                onChange={(e) => setCloseInput(e.target.value)}
                placeholder={orgName}
                aria-describedby="close-org-hint"
                style={{
                  height: 44,
                  padding: "0 14px",
                  border: "1.5px solid var(--line)",
                  borderRadius: "var(--r-md)",
                  background: "var(--surface)",
                  color: "var(--ink)",
                  fontFamily: "var(--font-body)",
                  fontSize: 14.5,
                  outline: "none",
                }}
              />
              <span
                id="close-org-hint"
                style={{ fontSize: 12.5, color: "var(--peach-deep)", marginTop: 2 }}
              >
                {l.closeTypeHint}
              </span>
            </div>

            <button
              onClick={() => setShowCloseConfirm(true)}
              disabled={!closeEnabled}
              style={{
                marginTop: 16,
                minHeight: 44,
                padding: "8px 22px",
                border: "1.5px solid var(--peach-deep)",
                borderRadius: "var(--r-pill)",
                background: closeEnabled ? "var(--peach-tint)" : "transparent",
                color: closeEnabled ? "var(--peach-deep)" : "var(--ink-faint)",
                fontFamily: "var(--font-body)",
                fontWeight: 600,
                fontSize: 14.5,
                cursor: closeEnabled ? "pointer" : "not-allowed",
                opacity: closeEnabled ? 1 : 0.5,
                transition: "opacity .15s",
                display: "block",
                textAlign: "start",
                lineHeight: 1.3,
              }}
            >
              {l.closeButton}
              <span style={{ display: "block", fontSize: 12, fontWeight: 500, marginTop: 2 }}>
                {l.closeButtonHint}
              </span>
            </button>
          </>
        )}
      </div>

      {/* Confirm — the action is the request email itself */}
      {showCloseConfirm && (
        <ConfirmModal
          title={l.closeConfirmTitle}
          body={l.closeConfirmBody}
          note={l.closeConfirmNote}
          confirmLabel={l.closeConfirmLabel}
          confirmHref={mailto}
          cancelLabel={common.cancel}
          onConfirm={() => {
            setClosureRequested(true);
            setShowCloseConfirm(false);
          }}
          onCancel={() => setShowCloseConfirm(false)}
        />
      )}
    </>
  );
}

// ---- Dispatcher + demo/live wrappers ----------------------------------------

/** Real org-scoped export when Supabase is configured, else the demo export. */
export function DataRights() {
  return shouldUseSupabase() ? <DataRightsLive /> : <DataRightsDemo />;
}

function DataRightsDemo() {
  const { locale } = useComfort();
  const l = L[locale];

  const sections: SectionOption[] = [
    { value: "members", label: l.sectionMembers },
    { value: "seats", label: l.sectionSeats },
    { value: "links", label: l.sectionLinks },
    { value: "consentLog", label: l.sectionConsentLog },
    { value: "children", label: l.sectionChildren },
  ];

  const accessRows: AccessRow[] = memberships.map((m) => ({
    email: accounts.find((a) => a.id === m.accountId)?.email ?? m.accountId,
    role: m.role,
  }));

  return (
    <DataRightsView
      orgName={organisation.name}
      orgId={organisation.id}
      exportNote={l.exportNote}
      sections={sections}
      state="ready"
      members={accessRows}
      onDownload={(format, section) => {
        const exp = buildOrgExport(
          {
            organisation,
            accounts,
            memberships,
            seats: fixtureSeats,
            childLinks,
            consents,
            children,
          },
          new Date().toISOString(),
        );
        if (format === "json") {
          triggerDownload(
            new Blob([toJson(exp)], { type: "application/json" }),
            `${organisation.id}-export.json`,
          );
          return;
        }
        const bundle = toCsvBundle(exp);
        const csv = bundle[section as keyof typeof bundle] ?? "";
        triggerDownload(
          new Blob([csv], { type: "text/csv" }),
          `${organisation.id}-${section}.csv`,
        );
      }}
    />
  );
}

function DataRightsLive() {
  const { orgId, orgName, orgType } = useOrg();
  const { locale } = useComfort();
  const l = L[locale];

  const [state, setState] = useState<LoadState>("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [snapshot, setSnapshot] = useState<LiveOrgExport | null>(null);
  const [accessRows, setAccessRows] = useState<AccessRow[]>([]);
  const [nonce, setNonce] = useState(0);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setErrorMessage("");

    void (async () => {
      try {
        const data = getPortalData();
        const [overview, members, roster, consentLog, licences] =
          await Promise.all([
            data.orgOverview(orgId),
            data.orgMembers(orgId),
            data.orgRoster(orgId),
            data.orgConsentLog(orgId),
            data.licenses(),
          ]);
        if (cancelled) return;

        const exp = buildLiveOrgExport(
          {
            orgId,
            orgName,
            orgType,
            overview,
            members,
            roster,
            consentLog,
            licence: licences.find((x) => x.orgId === orgId) ?? null,
          },
          new Date().toISOString(),
        );

        setSnapshot(exp);
        setAccessRows(members.map((m) => ({ email: m.email, role: m.role })));
        setState("ready");
      } catch (err) {
        if (cancelled) return;
        setSnapshot(null);
        setAccessRows([]);
        setErrorMessage(exportErrorCopy(err));
        setState("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, orgName, orgType, nonce]);

  const sections: SectionOption[] = useMemo(
    () => [
      { value: "members", label: l.sectionMembers },
      { value: "seats", label: l.sectionSeats },
      { value: "roster", label: l.sectionRoster },
      { value: "consentLog", label: l.sectionConsentLog },
    ],
    [l],
  );

  const summary = snapshot
    ? [
        `${snapshot.members.length} ${l.summaryMembers}`,
        `${snapshot.seats.length} ${l.summarySeats}`,
        `${snapshot.roster.length} ${l.summaryChildren}`,
        `${snapshot.consentLog.length} ${l.summaryConsents}`,
      ].join(" · ")
    : undefined;

  const isEmptyOrg =
    !!snapshot &&
    snapshot.members.length <= 1 &&
    snapshot.roster.length === 0 &&
    snapshot.consentLog.length === 0;

  return (
    <DataRightsView
      orgName={snapshot?.organisation.name || orgName}
      orgId={orgId}
      exportNote={l.exportNoteLive}
      sections={sections}
      state={state}
      errorMessage={errorMessage}
      onRetry={retry}
      summary={summary ? `${l.summaryLabel}: ${summary}` : undefined}
      emptyNote={isEmptyOrg ? l.emptyExportNote : undefined}
      members={accessRows}
      onDownload={(format, section) => {
        if (!snapshot) return;
        const exp: LiveOrgExport = {
          ...snapshot,
          generatedAt: new Date().toISOString(),
        };
        if (format === "json") {
          triggerDownload(
            new Blob([JSON.stringify(exp, null, 2)], { type: "application/json" }),
            `${orgId}-export.json`,
          );
          return;
        }
        const bundle = toLiveCsvBundle(exp);
        const csv = bundle[section as LiveExportSection] ?? "";
        triggerDownload(
          new Blob([csv], { type: "text/csv" }),
          `${orgId}-${section}.csv`,
        );
      }}
    />
  );
}
